import { useState, useEffect, useCallback, useRef } from "react";
import {
  getUsuariosParqueaderoService,
  createUsuarioParqueaderoService,
  updateUsuarioParqueaderoService,
  deleteUsuarioParqueaderoService,
} from "modules/copropiedades/services/usuariosParqueadero";
import {
  getCommerceId,
  getCommerceName,
  getUserCommerces,
  switchCommerce,
  initializeUserCommerce,
  getCurrentUser,
  hasModulePermission,
} from "modules/copropiedades/services/authService";

const isUserActive = (usuario) => {
  if (!usuario.end_date) return false;
  const now = new Date();
  const endDate = new Date(usuario.end_date);
  return endDate > now;
};

// ============================================================
// 🔥 NUEVA FUNCIÓN: Filtrar comercios por permiso en un módulo
// ============================================================
const filterCommercesByModulePermission = (commerces, moduleName) => {
  if (!commerces || !Array.isArray(commerces)) return [];

  const userData = getCurrentUser() || {};
  const permissions = userData.permissions || [];

  return commerces.filter((commerce) => {
    // Buscar si existe un permiso para este comercio en el módulo especificado
    const permiso = permissions.find(
      (p) => p.co_prop_id === commerce.id && p.co_table === moduleName
    );
    // Solo incluir si el permiso existe y "ver" es true
    return permiso && permiso.rol?.ver === true;
  });
};

export const useUsuariosParqueadero = () => {
  const [allUsuarios, setAllUsuarios] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [commerces, setCommerces] = useState([]);
  const [currentCommerceId, setCurrentCommerceId] = useState(null);
  const [currentCommerceName, setCurrentCommerceName] = useState("");
  const [hasPermission, setHasPermission] = useState(false);
  const [permisosUsuarios, setPermisosUsuarios] = useState({
    ver: false,
    crear: false,
    editar: false,
    eliminar: false,
  });
  const [initialized, setInitialized] = useState(false);
  const [stats, setStats] = useState({
    total: 0,
    activeCount: 0,
    expiredCount: 0,
  });

  const isMounted = useRef(true);
  const isLoadingRef = useRef(false);
  const hasLoaded = useRef(false);

  // ============================================================
  // 1. FUNCIONES AUXILIARES
  // ============================================================

  const loadCommerces = useCallback(async () => {
    try {
      const result = await getUserCommerces();
      if (result.success) {
        // 🔥 FILTRAR: Solo comercios donde el usuario tenga permiso "ver" en "usuarios"
        const filteredCommerces = filterCommercesByModulePermission(result.commerces, "usuarios");
        setCommerces(filteredCommerces);
        return filteredCommerces;
      }
      return [];
    } catch (error) {
      console.error("❌ Error al cargar comercios:", error);
      return [];
    }
  }, []);

  const initializeCommerce = useCallback(async () => {
    const existingCommerceId = getCommerceId();

    if (existingCommerceId) {
      const commercesList = await loadCommerces();
      const exists = commercesList.some((c) => c.id === existingCommerceId);
      if (exists) {
        return true;
      }
    }

    const result = await initializeUserCommerce();

    if (result.success) {
      // 🔥 FILTRAR: Solo comercios donde el usuario tenga permiso "ver" en "usuarios"
      const filteredCommerces = filterCommercesByModulePermission(result.commerces, "usuarios");

      // Si el comercio inicial no está en la lista filtrada, usar el primero de la lista filtrada
      let finalCommerceId = result.commerceId;
      let finalCommerceName = result.commerceName;

      if (
        !filteredCommerces.some((c) => c.id === finalCommerceId) &&
        filteredCommerces.length > 0
      ) {
        finalCommerceId = filteredCommerces[0].id;
        finalCommerceName = filteredCommerces[0].name;
        switchCommerce(finalCommerceId, finalCommerceName);
      }

      setCurrentCommerceId(finalCommerceId);
      setCurrentCommerceName(finalCommerceName || "");
      setCommerces(filteredCommerces);
      return true;
    } else {
      console.error("❌ Error al inicializar commerceId:", result.error);
      setError(result.error);
      return false;
    }
  }, [loadCommerces]);

  // ============================================================
  // 2. FUNCIÓN PARA CARGAR USUARIOS
  // ============================================================

  const loadUsuarios = useCallback(async () => {
    if (!hasPermission) {
      setError("No tiene permisos para ver usuarios");
      return;
    }

    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      let commerceId = getCommerceId();
      if (!commerceId) {
        if (commerces.length > 0) {
          const commerce = commerces[0];
          switchCommerce(commerce.id, commerce.name);
          setCurrentCommerceId(commerce.id);
          setCurrentCommerceName(commerce.name);
          commerceId = commerce.id;
        } else {
          const result = await initializeCommerce();
          if (result) {
            commerceId = getCommerceId();
          } else {
            setError("No se pudo obtener el comercio asociado al usuario");
            setLoading(false);
            isLoadingRef.current = false;
            return;
          }
        }
      }

      const result = await getUsuariosParqueaderoService();

      if (isMounted.current) {
        if (result.success) {
          setAllUsuarios(result.data);
          setUsuarios(result.data);
          setStats({
            total: result.total,
            activeCount: result.activeCount,
            expiredCount: result.expiredCount,
          });
          hasLoaded.current = true;
        } else {
          setError(result.error || "Error al cargar los usuarios");
        }
      }
    } catch (err) {
      if (isMounted.current) {
        setError(err.message || "Error al cargar los usuarios");
      }
    } finally {
      if (isMounted.current) {
        setLoading(false);
      }
      isLoadingRef.current = false;
    }
  }, [hasPermission, commerces, initializeCommerce]);

  // ============================================================
  // 3. CAMBIAR COMERCIO (después de loadUsuarios)
  // ============================================================

  const changeCommerce = useCallback(
    async (commerceId, commerceName) => {
      const result = switchCommerce(commerceId, commerceName);

      if (result.success) {
        setCurrentCommerceId(commerceId);
        setCurrentCommerceName(commerceName);

        // Recargar datos automáticamente
        hasLoaded.current = false;
        await loadUsuarios();

        return true;
      }
      return false;
    },
    [loadUsuarios]
  );

  // ============================================================
  // 4. INICIALIZACIÓN
  // ============================================================

  useEffect(() => {
    if (initialized) return;

    const init = async () => {
      try {
        const user = getCurrentUser();

        const commercesResult = await getUserCommerces();

        if (commercesResult.success) {
          // 🔥 FILTRAR: Solo comercios donde el usuario tenga permiso "ver" en "usuarios"
          const filteredCommerces = filterCommercesByModulePermission(
            commercesResult.commerces,
            "usuarios"
          );
          setCommerces(filteredCommerces);

          if (filteredCommerces.length === 1) {
            const commerce = filteredCommerces[0];
            switchCommerce(commerce.id, commerce.name);
            setCurrentCommerceId(commerce.id);
            setCurrentCommerceName(commerce.name);
          } else if (filteredCommerces.length > 1) {
            const existingId = getCommerceId();
            const existingName = getCommerceName();

            if (existingId && filteredCommerces.some((c) => c.id === existingId)) {
              setCurrentCommerceId(existingId);
              setCurrentCommerceName(existingName || "");
            } else {
              const commerce = filteredCommerces[0];
              switchCommerce(commerce.id, commerce.name);
              setCurrentCommerceId(commerce.id);
              setCurrentCommerceName(commerce.name);
            }
          } else {
            // No hay comercios con permiso "ver" en usuarios
            setError("No tiene permisos para ver usuarios en ninguna propiedad");
            setHasPermission(false);
            setInitialized(true);
            return;
          }
        }

        const canView = hasModulePermission("usuarios", "ver");

        if (!canView) {
          setError("No tiene permisos para ver usuarios");
          setHasPermission(false);
          setInitialized(true);
          return;
        }

        setHasPermission(true);

        setPermisosUsuarios({
          ver: true,
          crear: hasModulePermission("usuarios", "crear"),
          editar: hasModulePermission("usuarios", "editar"),
          eliminar: hasModulePermission("usuarios", "eliminar"),
        });

        setInitialized(true);
      } catch (error) {
        console.error("❌ Error en inicialización:", error);
        setError("Error al inicializar el módulo");
        setInitialized(true);
      }
    };

    init();

    return () => {
      isMounted.current = false;
    };
  }, []);

  // ============================================================
  // 5. CARGAR DATOS DESPUÉS DE LA INICIALIZACIÓN
  // ============================================================

  useEffect(() => {
    if (initialized && hasPermission && !hasLoaded.current) {
      loadUsuarios();
      loadCommerces();
    }
  }, [initialized, hasPermission, loadUsuarios, loadCommerces]);

  // ============================================================
  // 6. OTRAS FUNCIONES
  // ============================================================

  const applyFilters = useCallback(
    ({ startDate, endDate, status, search }) => {
      if (!allUsuarios.length) return;

      let filtered = [...allUsuarios];

      if (startDate) {
        const start = new Date(startDate);
        start.setHours(0, 0, 0, 0);
        filtered = filtered.filter((usuario) => {
          if (!usuario.start_date) return false;
          const userDate = new Date(usuario.start_date);
          return userDate >= start;
        });
      }

      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        filtered = filtered.filter((usuario) => {
          if (!usuario.start_date) return false;
          const userDate = new Date(usuario.start_date);
          return userDate <= end;
        });
      }

      if (status && status !== "all") {
        const isActive = status === "active";
        filtered = filtered.filter((usuario) => {
          const userActive = isUserActive(usuario);
          return isActive ? userActive : !userActive;
        });
      }

      if (search && search.trim() !== "") {
        const searchTerm = search.toLowerCase().trim();
        filtered = filtered.filter((usuario) => {
          const searchableFields = [
            usuario.plate?.toLowerCase() || "",
            usuario.name?.toLowerCase() || "",
            usuario.lastname?.toLowerCase() || "",
            usuario.cedula?.toLowerCase() || "",
            usuario.residential?.toLowerCase() || "",
            usuario.UserGoper?.toLowerCase() || "",
            usuario.start_date ? new Date(usuario.start_date).toLocaleString().toLowerCase() : "",
            usuario.end_date ? new Date(usuario.end_date).toLocaleString().toLowerCase() : "",
          ];
          return searchableFields.some((field) => field.includes(searchTerm));
        });
      }

      setUsuarios(filtered);
    },
    [allUsuarios]
  );

  const resetFilters = useCallback(() => {
    setUsuarios(allUsuarios);
  }, [allUsuarios]);

  const addUsuario = useCallback(
    async (newUsuario) => {
      if (!permisosUsuarios.crear) {
        return { success: false, error: "No tiene permisos para crear usuarios" };
      }

      if (currentUser?.username && !newUsuario.UserGoper) {
        newUsuario.UserGoper = currentUser.username;
      }

      try {
        const result = await createUsuarioParqueaderoService(newUsuario);

        if (result.success) {
          setAllUsuarios((prev) => {
            const updated = [...prev, result.data];
            const now = new Date();
            const activeCount = updated.filter((u) => new Date(u.end_date) > now).length;
            setStats({
              total: updated.length,
              activeCount: activeCount,
              expiredCount: updated.length - activeCount,
            });
            return updated;
          });
          setUsuarios((prev) => [...prev, result.data]);
          return { success: true, data: result.data };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [currentUser, permisosUsuarios]
  );

  const editUsuario = useCallback(
    async (updatedUsuario) => {
      if (!permisosUsuarios.editar) {
        return { success: false, error: "No tiene permisos para editar usuarios" };
      }

      try {
        const result = await updateUsuarioParqueaderoService(updatedUsuario);

        if (result.success) {
          setAllUsuarios((prev) => {
            const updated = prev.map((u) => (u.id === updatedUsuario.id ? updatedUsuario : u));
            const now = new Date();
            const activeCount = updated.filter((u) => new Date(u.end_date) > now).length;
            setStats({
              total: updated.length,
              activeCount: activeCount,
              expiredCount: updated.length - activeCount,
            });
            return updated;
          });
          setUsuarios((prev) => prev.map((u) => (u.id === updatedUsuario.id ? updatedUsuario : u)));
          return { success: true };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [permisosUsuarios]
  );

  const deleteUsuario = useCallback(
    async (id, plate) => {
      if (!permisosUsuarios.eliminar) {
        return { success: false, error: "No tiene permisos para eliminar usuarios" };
      }

      try {
        const result = await deleteUsuarioParqueaderoService(id, plate);

        if (result.success) {
          setAllUsuarios((prev) => {
            const updated = prev.filter((u) => u.id !== id);
            const now = new Date();
            const activeCount = updated.filter((u) => new Date(u.end_date) > now).length;
            setStats({
              total: updated.length,
              activeCount: activeCount,
              expiredCount: updated.length - activeCount,
            });
            return updated;
          });
          setUsuarios((prev) => prev.filter((u) => u.id !== id));
          return { success: true };
        } else {
          return { success: false, error: result.error };
        }
      } catch (error) {
        return { success: false, error: error.message };
      }
    },
    [permisosUsuarios]
  );

  const refreshData = useCallback(async () => {
    hasLoaded.current = false;
    await loadUsuarios();
  }, [loadUsuarios]);

  // ============================================================
  // 7. RETORNAR
  // ============================================================

  return {
    allUsuarios,
    usuarios,
    loading,
    error,
    stats,
    currentUser,
    commerces,
    currentCommerceId,
    currentCommerceName,
    hasPermission,
    permisosUsuarios,
    addUsuario,
    editUsuario,
    deleteUsuario,
    refreshData,
    applyFilters,
    resetFilters,
    changeCommerce,
    hasData: usuarios.length > 0,
  };
};
